import { AppRoutes, ROUTE_CONFIG } from '@/lib/constants/routes'
import { PERMISSIONS } from '@/lib/constants/permissions'

/**
 * Route configuration with priority hierarchy for permission-based routing
 */
interface RouteConfig {
	/** The permission string required to access this route */
	permission: string
	/** The route path to redirect to */
	route: string
	/** Priority level (lower numbers = higher priority) */
	priority: number
}

/**
 * Route configurations with priority hierarchy.
 * Priority order: Business Intelligence (1) → Recruiting (2) → Agent Management (3) → Core (4)
 */
const ROUTE_PRIORITY_CONFIGS: RouteConfig[] = [
	{
		permission: PERMISSIONS.BROKER_ADMIN_USER,
		route: AppRoutes.DASHBOARD,
		priority: 1,
	},
	{
		permission: PERMISSIONS.RECRUITING_USER,
		route: AppRoutes.RECRUITING_PROSPECT_PIPELINE,
		priority: 2,
	},
	{
		permission: PERMISSIONS.OFFICE_MANAGER_USER,
		route: AppRoutes.AGENT_MANAGEMENT,
		priority: 3,
	},
	{
		permission: PERMISSIONS.CORE_USER,
		route: AppRoutes.DASHBOARD,
		priority: 4,
	},
]

/**
 * Centralized permission guard for authentication and authorization logic
 * Single source of truth for permission checks across the application
 */
export class PermissionGuard {

	/**
	 * Checks if user has any permissions
	 * @param permissions - User permissions array or null if loading
	 * @returns True if user has at least one permission, false otherwise
	 */
	static hasPermissions(permissions: string[] | null): boolean {
		if (permissions === null) return false
		return permissions.length > 0
	}

	/**
	 * Checks if a given path is a public route (no auth required)
	 * @param path - The path to check
	 * @returns True if the path is public, false otherwise
	 */
	static isPublicRoute(path: string): boolean {
		return ROUTE_CONFIG.PUBLIC_ROUTES.some(
			(route) =>
				path === route || (route.length > 1 && path.startsWith(route))
		)
	}

	/**
	 * Checks if a given path requires permission validation
	 * @param path - The path to check for permission requirements
	 * @returns True if the path is permission-guarded, false otherwise
	 */
	static isPermissionGuarded(path: string): boolean {
		return ROUTE_CONFIG.PERMISSION_GUARDED_PATHS.has(path)
	}

	/**
	 * Determines if a user has access to a specific route based on their permissions
	 * @param permissions - Array of user permissions, or null if permissions haven't been loaded yet
	 * @param path - The route path to check access for
	 * @returns True if the user has access to the route, false otherwise
	 *
	 * @remarks
	 * - If the route is not permission-guarded, access is automatically granted
	 * - If permissions are null (not loaded), access is denied for security
	 * - For guarded routes, all required permissions must be present in the user's permissions array
	 */
	static hasRouteAccess(permissions: string[] | null, path: string): boolean {
		// If route not guarded, access allowed
		if (!this.isPermissionGuarded(path)) return true

		const required = ROUTE_CONFIG.PERMISSION_GUARDED_PATHS.get(path) || []
		return required.every(p => permissions!.includes(p))
	}

	/**
	 * Determines the appropriate route based on user permissions and route priorities
	 * @param permissions - Array of permission strings that the user has, or null if no permissions
	 * @returns The route string for the highest priority route the user has access to, or fallback route
	 *
	 * @remarks
	 * This function:
	 * - Returns the lowest priority fallback route if permissions are still loading
	 * - Returns unauthorized route if user has zero permissions
	 * - Filters available routes based on user permissions
	 * - Returns the fallback route if user has permissions but none match configured routes
	 * - Sorts matching routes by priority (lower number = higher priority) and returns the highest priority route
	 */
	static getRedirectRoute(permissions: string[] | null): string {

		if (!this.hasPermissions(permissions)) {
			console.error(
				'[UNAUTHORIZED] PermissionGuard - No permissions found, returning fallback route.'
			)
			return ROUTE_PRIORITY_CONFIGS[ROUTE_PRIORITY_CONFIGS.length - 1].route
		}

		// Find the highest priority route that the user has permission for
		const availableRoutes = ROUTE_PRIORITY_CONFIGS.filter(config =>
			permissions!.includes(config.permission)
		)

		if (availableRoutes.length === 0) {
			// User has permissions but none match our route configs, return fallback
			console.warn(
				'[PermissionGuard] No matching routes for user permissions, redirecting to fallback route.'
			)
			return ROUTE_PRIORITY_CONFIGS[ROUTE_PRIORITY_CONFIGS.length - 1].route
		}

		// Sort by priority (lower number = higher priority) and return the first one
		availableRoutes.sort((a, b) => a.priority - b.priority)
		return availableRoutes[0].route
	}
}
